import { useTranslation } from 'react-i18next';
import {
    Typography,
    Button,
    Select,
    MenuItem,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { BreakRuleTriggerType, BreakRuleActionType } from '../../../../Enums';
import DurationInput from '../DataviewSection/DurationInput';
import './BreakCalculation.scss';

const StyledSelect = (props) => (
    <Select
        MenuProps={{
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
            },
            getContentAnchorEl: null,
        }}
        variant="outlined"
        className="sue-select-full-width"
        defaultValue=""
        {...props}
    >
        {props.children}
    </Select>
);

/**
 * @param {{
 *  breakRules: *[],
 *  handleChangeSetting: (target: string, value: any) => void,
 *  disabled?: boolean
 * }} param0
 */
function BreakCalculation({ breakRules = [], handleChangeSetting, disabled = false }) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.functionsStep.scheduledHoursInput' });

    const triggerTypeSelectOptions = Object.keys(BreakRuleTriggerType).map((type) => ({
        label: t(`breakRuleTriggerType.${type}`),
        value: BreakRuleTriggerType[type],
    }));

    const actionTypeSelectOptions = Object.keys(BreakRuleActionType).map((action) => ({
        label: t(`breakRuleActionType.${action}`),
        value: BreakRuleActionType[action],
    }));

    const handleAddBreakRule = () => {
        const lastId = Math.max(...breakRules.map((rule) => rule.id));
        const emptyBreakRule = {
            triggerType: 0,
            triggerValue: 0,
            actionType: 0,
            actionValue: 0,
            id: lastId < 0 ? 0 : lastId + 1,
        };
        const newBreakRules = [...breakRules, emptyBreakRule];

        handleChangeSetting('breakRules', newBreakRules);
    };

    const handleDeleteBreakRule = (breakRuleId) => {
        const newBreakRules = breakRules.filter((rule) => rule.id !== breakRuleId);

        handleChangeSetting('breakRules', newBreakRules);
    };

    const handleChangeBreakRule = (target, input, breakRuleId) => {
        const newBreakRules = breakRules.map((rule) =>
            (rule.id === breakRuleId ? { ...rule, [target]: input } : rule));
        handleChangeSetting('breakRules', newBreakRules);
    };

    const generateBreakRuleLine = (breakRule) => (
        <div className="bc-break-rule-Line">
            <div className="bc-break-rule-item-large">
                <StyledSelect
                    id="break-rule-select"
                    renderValue={() => triggerTypeSelectOptions.find((x) => x.value === breakRule.triggerType)?.label}
                    value={triggerTypeSelectOptions.find((x) => x.value === breakRule.triggerType)}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                        event,
                    ) => handleChangeBreakRule('triggerType', event.target.value, breakRule.id)}
                    disabled={disabled}
                >
                    {triggerTypeSelectOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </div>
            <div className="bc-break-rule-item-medium">
                <DurationInput
                    key={`trigger-value-${breakRule.id}`}
                    onChange={(value) => handleChangeBreakRule('triggerValue', value, breakRule.id)}
                    value={breakRule.triggerValue}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    disabled={disabled}
                />

            </div>
            <div className="bc-break-rule-item-large">
                <StyledSelect
                    id="break-rule-select"
                    renderValue={() => actionTypeSelectOptions.find((x) => x.value === breakRule.actionType)?.label}
                    value={actionTypeSelectOptions.find((x) => x.value === breakRule.actionType)}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: number }>} */
                        event,
                    ) => handleChangeBreakRule('actionType', event.target.value, breakRule.id)}
                    disabled={disabled}
                >
                    {actionTypeSelectOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </div>
            <div className="bc-break-rule-item-medium">
                <DurationInput
                    key={`action-value-${breakRule.id}`}
                    onChange={(value) => handleChangeBreakRule('actionValue', value, breakRule.id)}
                    value={breakRule.actionValue}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={breakRule.actionValue === 0}
                    disabled={disabled}
                />
            </div>
            <div className="bc-break-rule-item-small">
                <Button
                    onClick={() => handleDeleteBreakRule(breakRule.id)}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </div>
        </div>
    );

    return (
        <div className="bc-container">
            <Typography className="bc-regular-label">
                {t('conditionsTitle')}
            </Typography>
            <div className="bc-break-rules-container">
                <div className="bc-break-rule-Line">
                    <div className="bc-break-rule-item-large bc-small-label">
                        {t('tableHeader1')}
                    </div>
                    <div className="bc-break-rule-item-medium bc-small-label">
                        {t('tableHeader2')}
                    </div>
                    <div className="bc-break-rule-item-large bc-small-label">
                        {t('tableHeader3')}
                    </div>
                    <div className="bc-break-rule-item-medium bc-small-label">
                        {t('tableHeader4')}
                    </div>
                </div>
                {breakRules.map((breakRule) => generateBreakRuleLine(breakRule))}
            </div>
            <Button
                size="small"
                color="primary"
                onClick={handleAddBreakRule}
                disabled={disabled}
            >
                {t('buttonLabel')}
            </Button>
        </div>
    );
}
export default BreakCalculation;
